import { useSuspenseQuery } from '@apollo/client';
import { Suspense, useState, useTransition } from 'react';

import { ErrorBoundary } from '@xing-com/crate-core-error-boundary';
import { WarningMessageBox } from '@xing-com/crate-entity-pages-common';
import { usePageContext } from '@xing-com/crate-entity-pages-entity-pages-shared-context-entity-page-context';
import type { FilterStateSelectedItemsType } from '@xing-com/crate-entity-pages-jobs';
import { JobsFilter } from '@xing-com/crate-entity-pages-jobs';
import { useLoginState } from '@xing-com/crate-hooks-use-login-state';

import type {
  JobsDetailQuery,
  JobsDetailQueryVariables,
} from './graphql/queries/jobs-detail-query.gql-types';
import { JobsDetailDocument } from './graphql/queries/jobs-detail-query.gql-types';
import { JobsDetail, JobsDetailSkeleton } from './jobs-detail';

/** The error flag of React Apollo is not reliable when using SSR, therefore we check if there are errors like this  */
const isError = (data: JobsDetailQuery | undefined) => {
  return !data?.jobSearchByQuery?.collection;
};

const LIST_LIMIT = 10;

const JobsDetailContainerInner: React.FC = () => {
  const { companyId } = usePageContext().pageContext;
  const { isLoggedIn } = useLoginState();
  const [isPending, startTransition] = useTransition();
  const [offset, setOffset] = useState<number>(LIST_LIMIT);
  const { data, error, refetch, fetchMore } = useSuspenseQuery(
    JobsDetailDocument,
    {
      variables: {
        isLoggedIn,
        limit: LIST_LIMIT,
        query: {
          filter: {
            company: { id: [companyId!] },
            publishToCompany: null,
          },
        },
      },
      errorPolicy: 'all',
      notifyOnNetworkStatusChange: true,
    }
  );

  const handlePagination = () => {
    startTransition(() => {
      fetchMore({
        variables: {
          offset,
        },
        updateQuery: (prev: any, { fetchMoreResult }) => {
          if (!fetchMoreResult) return prev;
          return {
            jobSearchByQuery: {
              ...prev?.jobSearchByQuery,
              collection: [
                ...(prev?.jobSearchByQuery?.collection || []),
                ...(fetchMoreResult.jobSearchByQuery?.collection || []),
              ],
            },
          };
        },
      }).then(() => {
        setOffset((offset: number) => offset + LIST_LIMIT);
      });
    });
  };

  const handleFilterChange = async (
    selectedItems: FilterStateSelectedItemsType,
    keywords: string
  ) => {
    const variables: JobsDetailQueryVariables = {
      isLoggedIn,
      query: {
        filter: {
          company: { id: [companyId!] },
        },
      },
    };

    if (keywords) {
      variables.query.keywords = keywords + '*';
    }
    if (variables.query.filter) {
      if (selectedItems.careerLevels) {
        variables.query.filter.careerLevel = { id: selectedItems.careerLevels };
      }
    }

    await refetch(variables);

    setOffset(LIST_LIMIT);
  };

  const aggregations = data?.jobSearchByQuery?.aggregations;

  if (error || isError(data)) return <ErrorFallback onReload={refetch} />;

  return (
    <>
      {isLoggedIn && (
        <JobsFilter
          onFiltersChanges={handleFilterChange}
          // @ts-expect-error TODO: fix this type
          aggregations={aggregations}
        />
      )}
      <JobsDetail
        onLazyLoad={handlePagination}
        boundedTotal={data?.jobSearchByQuery?.boundedTotal}
        searchQuery={data?.jobSearchByQuery?.searchQuery}
        paginationLoading={isPending}
        // @ts-expect-error TODO: fix this type
        jobResults={data?.jobSearchByQuery?.collection}
      />
    </>
  );
};

const Fallback: React.FC = () => {
  return <JobsDetailSkeleton />;
};

const ErrorFallback: React.FC<{
  onReload?: () => void;
}> = ({ onReload = () => window.location.reload() }) => {
  return (
    <div data-testid={'errorContainer'}>
      <WarningMessageBox
        headerText="EP_ERROR_HEADER"
        bodyText="EP_ERROR_BODY"
        buttonText="EP_ERROR_RELOAD_CTA"
        onClick={onReload}
      />
    </div>
  );
};

export const JobsDetailContainer: React.FC = () => {
  return (
    <ErrorBoundary onError={() => <ErrorFallback />}>
      <Suspense fallback={<Fallback />}>
        <JobsDetailContainerInner />
      </Suspense>
    </ErrorBoundary>
  );
};
